import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { AppDispatch, RootState } from "@app/store";
import { fetchTrackOrder } from "@app/store/sharedThunks";
import { environment } from "@app/config/environment";
import { AsyncState } from "@app/shared/hooks/useAsync";
import { OrderSummary } from "@app/domain";
import { toOrderSummary } from "../adapters/toOrderSummary";

/**
 * Powers Home and Order Details — both read the same Redux `order` slice
 * (kept live by the initial `fetchTrackOrder` thunk AND by `useSocketIo`'s
 * push updates, see `src/hooks/socket.ts`), so navigating between them never
 * re-fetches, and status/driver-position pushes show up on both without
 * either page doing anything extra.
 *
 * `trackCode` is the opaque URL identifier (legacy `/:trackCode`,
 * `/details/:trackCode`) — NOT the real internal order id
 * (`OrderSummary.id`, from `state.order.orderId`).
 *
 * Dispatch condition mirrors the legacy `page/main.tsx`/`page/details.tsx`
 * effect exactly: re-fetch only when the track code or dropoff-tracking mode
 * actually changes, not on every render.
 */
export function useOrderSummary(trackCode: string, isDropoffTracking = false): AsyncState<OrderSummary> {
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();
  const language = searchParams.get("language");

  const order = useSelector((state: RootState) => state.order);
  const customer = useSelector((state: RootState) => state.customer);
  const service = useSelector((state: RootState) => state.service);
  const setting = useSelector((state: RootState) => state.setting);

  const key = `${trackCode}::${isDropoffTracking}`;
  const requestedKeyRef = useRef<string | null>(null);

  // Computed at render time (not just inside the effect below) so a fresh
  // mount that lands on already-loaded data — e.g. navigating Home ->
  // Details for the same order — reports "success" on its very first
  // render. The effect below only mutates a ref, which doesn't itself
  // trigger a re-render, so `isCurrent` must not depend solely on it.
  const alreadyLoaded =
    order.trackOrderCode === trackCode &&
    order.isDropoffTracking === isDropoffTracking &&
    order.orderFetchStatus === "succeeded";

  useEffect(() => {
    if (alreadyLoaded) {
      requestedKeyRef.current = key;
      return;
    }
    if (requestedKeyRef.current === key) return;
    requestedKeyRef.current = key;

    let restUrl: string | undefined;
    if (environment.isDevelopment) restUrl = environment.localRestUrl;

    dispatch(fetchTrackOrder({ trackCode, language, restUrl, dropoffTracking: isDropoffTracking }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackCode, isDropoffTracking]);

  const isCurrent = alreadyLoaded || requestedKeyRef.current === key;

  if (!isCurrent || order.orderFetchStatus === "loading" || order.orderFetchStatus === "idle") {
    return { status: "loading", data: undefined, error: undefined };
  }

  if (order.orderFetchStatus === "failed") {
    return { status: "error", data: undefined, error: new Error(order.error?.message ?? "Failed to load order") };
  }

  return {
    status: "success",
    data: toOrderSummary({ order, customer, service, setting, isDropoffTracking }),
    error: undefined,
  };
}
